import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import HistoryIconLink from "../../../components/change-history/HistoryIconLink";
import HeaderPage from "../../../components/lines-managements/header/HeaderPage";
import DeleteModal from "../../../components/ui/DeleteModal";
import Table, { Row } from "../../../components/ui/table";
import Pagination from "../../../components/ui/table/Pagination";
import Can from "../../../components/users/permissions/Can";
import useCan from "../../../components/users/permissions/useCan";
import usePageIndex from "../../../hooks/usePageIndex";
import { useSearch } from "../../../hooks/useSearch";
import { IGTFSTripStop } from "../../../interfaces/endpoints/IGTFS.types";
import { deleteGTFSTripStop, getGTFSTripStops } from "../../../services/GTFS.service";
import { withDayMarker } from "../../../utils/tripDuration";

const COLUMNS = ["ID", "Orari", "Stacioni", "Mbërritja", "", "Veprimet"];
const PAGE_SIZE = 10;

const arrivalLabel = (row: IGTFSTripStop) =>
  withDayMarker(row.arrivalClock, row.arrivalDayOffset) +
  (row.fallsAfterTripArrival ? " · pas mbërritjes së udhëtimit" : "");

const tripLabel = (row: IGTFSTripStop) =>
  `${row.tripId} · ${row.lineName ?? ""}${row.tripDepartureTime ? ` (${row.tripDepartureTime})` : ""}`;

// Line names can be long and the table never wraps, so the cell caps itself and keeps the full
// text in its title.
const capped = (text: string) => (
  <span className="block max-w-sm truncate" title={text}>
    {text}
  </span>
);

const GTFSTripStops: React.FC = () => {
  const canCreate = useCan("gtfs.create");
  const { response, loading, handlePageChange, page, setResponse } = useSearch(getGTFSTripStops, {
    withQuerySearch: true
  });
  const { pageIndex } = usePageIndex();

  return (
    <>
      <HeaderPage
        {...(canCreate && {
          link: {
            to: "create",
            children: "Shto ndalesë",
            state: ["Menaxhimi i GTFS", "Ndalesat", "Shto"]
          }
        })}
      />
      <div className="flex flex-col" style={{ height: "calc(100vh - 11rem)" }}>
        <Table titles={COLUMNS} style="hover" loading={loading}>
          {!loading && response?.data?.result?.length ? (
            response.data.result.map((row) => (
              <Row
                key={row.id}
                id={row.id}
                trip={capped(tripLabel(row))}
                station={row.stationName ?? String(row.stationId)}
                arrival={capped(arrivalLabel(row))}
                history={<HistoryIconLink entityName="GTFSTripStop" entityId={row.id} />}
                actions={
                  <div className="flex items-center gap-2">
                    <Can permissions="gtfs.update">
                      <Link
                        className="p-2.5 md:p-1"
                        state={{
                          crumbs: ["Menaxhimi i GTFS", "Ndalesat", "Ndrysho"],
                          page: pageIndex
                        }}
                        to={`edit/${row.id}`}
                      >
                        <PencilSquareIcon className="w-5 h-5 md:w-4" />
                      </Link>
                    </Can>
                    <Can permissions="gtfs.delete">
                      <DeleteModal
                        params={row.id}
                        title="Konfirmo fshirjen"
                        paragraph="Ndalesa hiqet nga feed-i i radhës; udhëtimi mbetet me dy ndalesat e tij."
                        handleDelete={deleteGTFSTripStop}
                        successMessage="Ndalesa u fshi"
                        errorMessage="Fshirja dështoi"
                        loadingMessage="Duke fshirë..."
                        onSuccess={() => {
                          setResponse((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  data: {
                                    ...prev.data,
                                    result: (prev.data.result ?? []).filter((item) => item.id !== row.id),
                                    totalCount: (prev.data.totalCount ?? 1) - 1
                                  }
                                }
                              : prev
                          );
                        }}
                      />
                    </Can>
                  </div>
                }
              />
            ))
          ) : (
            <tr>
              <td colSpan={COLUMNS.length} className="text-center py-4">
                {loading ? "Duke u ngarkuar..." : "Nuk u gjet asnjë ndalesë"}
              </td>
            </tr>
          )}
        </Table>
        <Pagination
          onPageChange={handlePageChange}
          totalCount={response?.data?.totalCount || 0}
          length={response?.data?.result?.length || 0}
          page={page}
          pageSize={PAGE_SIZE}
        />
      </div>
    </>
  );
};

export default GTFSTripStops;
